import { Module } from 'vuex'
import $fetch from '@/api'
import { openMessage } from '@/utils/system'
import dayjs from 'dayjs'
import { getDayBeweenDate } from '@/utils'
import { openRepeatDataHandler } from '@/components/repeat_data_handler/repeat_data_handler.ts'
import { openRepeatDataHandlerCardCode } from '@/components/repeat_data_handler_cardcode/repeat_data_handler_cardcode.ts'

const state: Module<{
    shopInfo: any,
    userInfo: any,
    staffInfo: any,
    oldLsShopInfo: any,
    token: string,
    isRenewOpen: boolean,
    isImportingMemberData: boolean,
    isImportingGoodsData: boolean,
    isCheckingImportMemberData: boolean,
    isCheckingImportGoodsData: boolean,
    isBadGoodsNum: number,
    recordId: number
}, {}> =  {
    namespaced: true,
    state: {
        shopInfo: {
            remain_days: 0
        },
        userInfo: {},
        staffInfo: {},
        oldLsShopInfo: {},
        token: '',
        isRenewOpen: false,
        isImportingMemberData: false,
        isImportingGoodsData: false,
        isCheckingImportMemberData: false,
        isCheckingImportGoodsData: false,
        isBadGoodsNum: 0,
        recordId: 0
    },
    mutations: {
        setUserInfo(states, userInfo: any) {
            states.userInfo = userInfo
        },

        setShopInfo(states, shopInfo: any) {
            const reg = /\d{4}-\d{2}-\d{2}/g
            const dayHasMember = getDayBeweenDate(dayjs().format('YYYY-MM-DD HH:mm:ss'), shopInfo.operate_time)
            const dayLooseMember = shopInfo.remain_days || getDayBeweenDate(shopInfo.expired_time, dayjs().format('YYYY-MM-DD HH:mm:ss'))
            // tslint:disable-next-line:no-unused-expression
            shopInfo.expired_time && (shopInfo.expired_time = shopInfo.expired_time.match(reg)[0])
            // tslint:disable-next-line:no-unused-expression
            shopInfo.operate_time && (shopInfo.operate_time = shopInfo.operate_time.match(reg)[0])
            shopInfo.day_has_member = dayHasMember > 0 ? dayHasMember : 0
            shopInfo.day_loose_member = dayLooseMember > 0 ? dayLooseMember : 0
            shopInfo.real_day_loose_member = dayLooseMember
            states.shopInfo = shopInfo
        },

        setStaffInfo(states, staffInfo: any) {
            states.staffInfo = staffInfo
        },

        setToken(states, token: string) {
            states.token = token
        },

        setRenewOpenState(states, flag) {
            states.isRenewOpen = flag
        },

        setIsImportingMemberData(states, flag = false) {
            states.isImportingMemberData = flag
        },

        setIsImportingGoodsData(states, flag = false) {
            states.isImportingGoodsData = flag
        },

        setGoodsRecordId(states, data) {
            states.recordId = data
        },

        setMemberImportInfo(states, data) {
            states.isImportingMemberData = (Object.keys(data).length > 0 && data.import_status !== 2)
            states.oldLsShopInfo = data
        },

        setBadGoodsNum(states, data) {
            states.isBadGoodsNum = data
        }
    },
    getters: {
        canIGoIn: states => () => {
            if (!states.shopInfo.id || !states.token || !states.staffInfo.id) {
                return false
            } else {
                return true
            }
        },

        canIUse: states => (...args: number[]) => {
            const { staffInfo } = states
            if (staffInfo.permission_list == null || args.length === 0) {
                return true
            }
            const permissionsList = staffInfo.permission_list
            return args.every(id => {
                if (id == null || id === 0) {
                    return true
                }
                return permissionsList.includes(id)
            })
        },

        isShiYong(states) {
            return states.shopInfo.use_status === 0
        },

        isBiaoZhun(states) {
            return states.shopInfo.shop_version === 0
        },

        isZhuanye(states) {
            return states.shopInfo.shop_version === 1
        },

        isQijian(states) {
            return states.shopInfo.shop_version === 2
        },

        getCurrentPayChannel(states) {
            return function(targetPayment: number) {
                const { shopInfo } = states
                const defaultChannel = {
                    channel_type: 0,
                    status: 1,
                    merchant_no: ''
                }
                if (shopInfo.id != null) {
                    const curChannel = shopInfo.pay_channel.find(channel => channel.channel_type === (targetPayment || shopInfo.online_payment - 1) && channel.status === 1)
                    return curChannel || defaultChannel
                } else {
                    return defaultChannel
                }
            }
        }
    },
    actions: {
        async init({dispatch, commit}, data: {token?: string}) {
            if (data.token) {
                commit('setToken', data.token)
            } else {
                await dispatch('getAndSetToken')
            }
            await Promise.all([dispatch('getAndSetUserInfo'), dispatch('getAndSetShopInfo'), dispatch('getMemberImportInfo')])
            await dispatch('getAndSetStaffInfo')
        },

        getAndSetToken({commit}) {
            return new Promise((resolve, reject) => {
                $fetch.post({
                    url: '/user/gettoken'
                }).then(data => {
                    commit('setToken', data.token)
                    resolve(data.token)
                }).catch(e => {
                    openMessage('error', e.message)
                    reject()
                })
            })
        },

        getAndSetShopInfo({ commit }) {
            return new Promise((resolve, reject) => {
                $fetch.post({
                    url: '/shop/getshopinfo'
                }).then(data => {
                    commit('setShopInfo', data)
                    resolve(data)
                }).catch(e => {
                    openMessage('error', e.message)
                    reject()
                })
            })
        },

        getAndSetUserInfo({commit}) {
            return new Promise((resolve, reject) => {
                $fetch.post({
                    url: '/user/getuserinfo',
                }).then(data => {
                    commit('setUserInfo', data.user)
                    resolve(data.user)
                }).catch(e => {
                    openMessage('error', e.message)
                    reject()
                })
            })
        },

        getAndSetStaffInfo({commit}) {
            return new Promise((resolve, reject) => {
                $fetch.post({
                    url: '/staff/getstaffinfo'
                }).then(data => {
                    commit('setStaffInfo', data)
                    resolve(data)
                }).catch(e => {
                    openMessage('error', e.message)
                    reject()
                })
            })
        },

        getMemberImportInfo({commit}) {
            return $fetch.post({
                url: '/membership/migration/getlsmemberimportinfo'
            }).then((data) => {
                commit('setMemberImportInfo', data)
            }).catch(e => openMessage('error', e.message || '获取导入信息失败'))
        },

        checkImportData({state: states}) {
            if (states.isCheckingImportMemberData) {
                return Promise.resolve()
            }
            states.isCheckingImportMemberData = true

            function check() {
                return $fetch.post({
                    url: '/membership/migration/getlsmemberimportinfo'
                })
            }

            function importSuccess(timer, resolve, showMsg = true) {
                timer && clearInterval(timer)
                states.isImportingMemberData = false
                states.isCheckingImportMemberData = false
                showMsg && openMessage('success', '导入成功')
                resolve()
            }

            function importError(timer, reject, e, defaultMsg = '') {
                timer && clearInterval(timer)
                states.isImportingMemberData = false
                states.isCheckingImportMemberData = false
                defaultMsg && openMessage('error', e.message)
                reject(e)
            }

            function handlerRepeatData(recordId: number | string, isAcceptImportedData: boolean) {
                return $fetch.post({
                    url: '/membership/migration/deduplicateimportedlsmembers',
                    params: {
                        record_id: recordId,
                        accept_imported_data: isAcceptImportedData
                    }
                })
            }

            async function startCheck(resolve, reject, timer) {
                try {
                    const result = await check()
                    if (Object.keys(result).length <= 0) {
                        importSuccess(timer, resolve)
                    } else {
                        switch (result.import_status) {
                            case 0: // 导入中...
                                console.log('导入中', result)
                                break
                            case 1: // 导入重复数据待处理
                                console.log('导入重复数据待处理', result)
                                timer && clearInterval(timer)
                                states.isCheckingImportMemberData = false
                                if (result.duplicated_member_pairs && result.duplicated_member_pairs.length) {
                                    const instance = openRepeatDataHandler({
                                        isReturnInstance: true,
                                        props: { repeatData: result.duplicated_member_pairs }
                                    }) as any
                                    instance.$on('confirm', async (selectedVal) => {
                                        /**
                                         * selectedVal
                                         * 0: 使用导入数据
                                         * 1: 使用原始数据
                                         */
                                        try {
                                            await handlerRepeatData(result.record.id, selectedVal === 0)
                                            startCheck(resolve, reject, timer)
                                            if (
                                                (result.duplicated_card_code_member_pairs && result.duplicated_card_code_member_pairs.length) ||
                                                (result.duplicated_old_ls_pairs && result.duplicated_old_ls_pairs.length)
                                            ) {
                                                const cardcodeInstance = openRepeatDataHandlerCardCode({
                                                    isReturnInstance: true,
                                                    props: {
                                                        repeatCardData: result.duplicated_card_code_member_pairs,
                                                        repeatOldData: result.duplicated_old_ls_pairs
                                                    }
                                                }) as any
                                            }
                                        } catch (e) {
                                            importError(timer, reject, e, '处理重复数据失败')
                                        }
                                    })
                                    instance.$on('cancel', () => {
                                        importSuccess(timer, resolve, false)
                                    })
                                } else if (
                                        (result.duplicated_card_code_member_pairs && result.duplicated_card_code_member_pairs.length) ||
                                        (result.duplicated_old_ls_pairs && result.duplicated_old_ls_pairs.length)
                                    ) {
                                    const cardcodeInstance = openRepeatDataHandlerCardCode({
                                        isReturnInstance: true,
                                        props: {
                                            repeatCardData: result.duplicated_card_code_member_pairs,
                                            repeatOldData: result.duplicated_old_ls_pairs
                                        }
                                    }) as any
                                }

                                break
                            case 2: // 没有正在导入的任务
                                console.log('没有正在导入的任务', result)
                                importSuccess(timer, resolve)
                                if (
                                    (result.duplicated_card_code_member_pairs && result.duplicated_card_code_member_pairs.length) ||
                                    (result.duplicated_old_ls_pairs && result.duplicated_old_ls_pairs.length)
                                ) {
                                    const cardcodeInstance = openRepeatDataHandlerCardCode({
                                        isReturnInstance: true,
                                        props: {
                                            repeatCardData: result.duplicated_card_code_member_pairs,
                                            repeatOldData: result.duplicated_old_ls_pairs
                                        }
                                    }) as any
                                }
                                break
                            default:
                                break
                        }
                    }
                } catch (e) {
                    importError(timer, reject, e, '检测导入状态失败')
                }
            }

            return new Promise((resolve, reject) => {
                let timer
                const fn = () => startCheck(resolve, reject, timer)
                timer = setInterval(fn, 1000)
                fn()
            })
        },

        checkImportgoodssheet({commit}, data) {
            return $fetch.post({
                url: '/goods/migration/importgoodssheet',
                params: {
                    sheet_url: data
                }
            }).then(res => {
                commit('setGoodsRecordId', res.record_id)
            }).catch(e => openMessage('error', e.message || '导入表格失败'))
        },

        checkImportGoodsData({state: states}) {
            if (states.isCheckingImportGoodsData) {
                return Promise.resolve()
            }
            states.isCheckingImportGoodsData = true

            function check(id) {
                return $fetch.post({
                    url: '/goods/migration/getgoodsimportstatus',
                    params: {
                        record_id: id
                    }
                })
            }

            // tslint:disable-next-line: variable-name
            function checkTableDataresult(imported_fail) {
                states.isBadGoodsNum = imported_fail || 0
            }

            function importSuccess(timer, resolve, result, showMsg = true) {
                timer && clearInterval(timer)
                states.isImportingGoodsData = false
                states.isCheckingImportGoodsData = false
                const { imported_fail, import_status } = result || {}
                if (import_status === 1 && imported_fail > 0) {
                    checkTableDataresult(imported_fail || 0)
                } else {
                    showMsg && openMessage('success', '导入成功')
                }
                resolve()
            }

            function importError(timer, reject, e, defaultMsg = '') {
                timer && clearInterval(timer)
                states.isImportingGoodsData = false
                states.isCheckingImportGoodsData = false
                defaultMsg && openMessage('error', e.message)
                reject(e)
            }


            async function startCheck(resolve, reject, timer) {
                try {
                    const result = await check(states.recordId)
                    switch (result.import_status) {
                        case 0: // 导入中...
                            console.log('导入中', result)
                            break
                        case 1: // 没有正在导入的任务
                            console.log('没有正在导入的任务', result)
                            importSuccess(timer, resolve, result)
                            break
                        default:
                            break
                    }
                } catch (e) {
                    importError(timer, reject, e, '检测导入状态失败')
                }
            }

            return new Promise((resolve, reject) => {
                let timer
                const fn = () => startCheck(resolve, reject, timer)
                timer = setInterval(fn, 1000)
                fn()
            })
        }
    }
}

export default state
